import { useState, useRef, useEffect } from "react";
import axios from "axios";
import Map, { NavigationControl } from "react-map-gl";
import useSupercluster from "use-supercluster";
import { MapMarker, Sidebar } from "./";
import "mapbox-gl/dist/mapbox-gl.css";

const pinToPoint = (c, index) => {
  const [latitude, longitude] = c.coordinates ? c.coordinates.split(",") : [];

  return {
    type: "Feature",
    properties: { ...c, id: index, selected: false },
    geometry: {
      type: "Point",
      coordinates: [parseFloat(longitude), parseFloat(latitude)],
    },
  };
};

function useMarkers(pins, selectedId, bounds, zoom) {
  const [points, setPoints] = useState([]);

  useEffect(() => {
    setPoints(pins.map(pinToPoint));
  }, [pins]);

  const { clusters, supercluster } = useSupercluster({
    points,
    bounds,
    zoom,
    options: {
      radius: 125,
      nodeSize: 64,
      maxZoom: 20,
      map: ({ cases_count }) => ({ cases_count }),
      reduce: (acc, { cases_count }) => {
        acc.cases_count += cases_count;
        return acc;
      },
    },
  });

  return {
    markers: clusters.map((c) => ({
      ...c,
      properties: {
        ...c.properties,
        selected: c.properties.id == selectedId,
      },
    })),
    supercluster,
  };
}

export default function OverviewMap({ pins, lat, lng, zoom: defaultZoom }) {
  const [bounds, setBounds] = useState(null);
  const [zoom, setZoom] = useState(defaultZoom);
  const [localCases, setLocalCases] = useState([]);
  const [selectedPinId, setSelectedPinId] = useState();
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const mapRef = useRef(null);
  const { markers, supercluster } = useMarkers(
    pins,
    selectedPinId,
    bounds,
    zoom
  );

  const getVictims = async (coordinates) => {
    try {
      return await axios.get(
        `${process.env.NEXT_PUBLIC_API_BASE_URL}/case/?coordinates=${coordinates}`
      );
    } catch (error) {
      console.error(error);
    }
  };

  const handleCloseButtonClick = () => {
    setShowSidebar(false);
  };

  function onMapChange(map) {
    setZoom(map.getZoom());
    const bounds = map.getBounds();
    const nw = bounds.getNorthWest();
    const se = bounds.getSouthEast();
    setBounds([nw.lng, se.lat, se.lng, nw.lat]);
  }

  function onClusterMarkerClick(marker) {
    const zoom = Math.min(supercluster.getClusterExpansionZoom(marker.id), 20);
    mapRef.current.flyTo({
      center: marker.geometry.coordinates,
      zoom,
    });
  }

  function onPinMarkerClick(marker) {
    const map = mapRef.current;
    map.flyTo({
      center: marker.geometry.coordinates,
    });
    setCasesAtMarker(marker);
  }

  function onEmptyClick() {
    setSelectedPinId(undefined);
    setShowSidebar(false);
    setSelectedCity(undefined);
    setSelectedState(undefined);
  }

  async function setCasesAtMarker(marker) {
    const id = marker.properties.id;
    setSelectedCity(marker.properties.city);
    setSelectedState(marker.properties.state);
    setSelectedPinId(id);
    const selectedPoint = markers.filter(
      (a) => a.properties.id === parseInt(id)
    )[0];
    if (!selectedPoint) {
      return;
    } else {
      setShowSidebar(true);
    }

    const coordinates = selectedPoint.properties.coordinates;
    const response = await getVictims(coordinates);
    setLocalCases(response.data.results);
  }

  return (
    <div
      className="relative lg:flex"
      style={{ height: "472px", margin: "0 auto", width: "100%" }}
    >
      {showSidebar && (
        <Sidebar
          selectedCity={selectedCity}
          selectedState={selectedState}
          localCases={localCases}
          handleCloseButtonClick={handleCloseButtonClick}
        />
      )}

      <Map
        cooperativeGestures={true}
        fadeDuration={0}
        initialViewState={{
          longitude: lng || -98.35,
          latitude: lat || 39.82,
          zoom: zoom || 3.5,
        }}
        mapboxAccessToken={process.env.NEXT_PUBLIC_MAPBOX_API_TOKEN}
        mapStyle="mapbox://styles/mapbox/streets-v9"
        onLoad={(e) => {
          onMapChange(e.target);
        }}
        onMoveEnd={(e) => {
          onMapChange(e.target);
        }}
        onClick={(e) => {
          if (!e.originalEvent.defaultPrevented) {
            onEmptyClick();
          }
        }}
        ref={mapRef}
      >
        <NavigationControl className="navigation-control" showCompass={false} />
        {markers.map((marker) => {
          const { id: markerId, properties } = marker;
          const {
            id: propertyId,
            cases_count: caseCount,
            cluster,
            selected,
          } = properties;
          return (
            <MapMarker
              key={`cluster-${cluster ? markerId : propertyId}`}
              isCluster={cluster}
              marker={marker}
              caseCount={caseCount}
              selected={selected}
              onClick={(e) => {
                e.originalEvent.preventDefault();
                cluster
                  ? onClusterMarkerClick(marker)
                  : onPinMarkerClick(marker);
              }}
            />
          );
        })}
      </Map>
    </div>
  );
}
